import { type DisplayDocument, Document } from '@startuptools/business-ui/document/Document';
import { type ComponentProps, Suspense, useState } from 'react';
import { DATE_FORMAT_LUXON } from '@startuptools/common/common';
import { DateTime } from 'luxon';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import {
  FileDocument,
  type GqlDocGenValidateMeetingQuery,
  MeetingDocument,
  useActiveCompanyStripeSubLazyQuery,
  useCompanySuspenseQuery,
  useDocGenValidateMeetingLazyQuery,
  useDocGenerateMeetingMinutesMutation,
  useMeetingPaidForConcernsLazyQuery,
} from '../../../../graphql/react-operations';
import { apiClient } from '../../../../common/http';
import { checkNeedsPayment, hasValidatorErrors } from '../../utils';
import { MeetingValidatorErrorDialog } from '../MeetingValidatorErrorDialog';
import {
  GqlDocSystemTagKind,
  GqlDocumentSignatureMethod,
  type GqlMeeting,
  GqlMeetingKind,
} from '../../../../graphql/base-types.graphql';
import { MeetingMinutesWithUploadsDocument } from './MeetingMinutesWithUploads.graphql';
import { Divider } from '@startuptools/ui/Divider';
import { DocumentSignatures } from '../../../../components/document-signatures/DocumentSignatures';
import { InnerStripe } from '../../../../components/stripe/Stripe';
import { OptionsDocumentsDocument } from '../../../stock-options/common/components/main-steps/options-documents/OptionsDocuments.graphql';
import { DocGenRelationName } from '@startuptools/common/doc-gen';
import { useApolloClient } from '@apollo/client';
import { SiDocumentsShowDocument } from '../../../share-issues/component/DocumentsShow.graphql';
import { WarrantProgramDocument } from '../../../stock-options/WarrantProgram.graphql';
import { AuthorizationDocument } from '../../../authorizations/Authorization.graphql';
import { ShareIssueDocument } from '../../../share-issues/ShareIssue.graphql';
import { useNavigate } from '../../../../components/Navigate';

const meetingFilename = (meeting: Pick<GqlMeeting, 'startTime' | 'kind' | 'number'>): string => {
  const meetingDate = DateTime.fromISO(meeting.startTime).toFormat(DATE_FORMAT_LUXON);
  const filename = {
    [GqlMeetingKind.BoardMeeting]: `Protokoll - Styrelsemöte #${meeting.number} - ${meetingDate}`,
    [GqlMeetingKind.ExtraShareholdersMeeting]: `Protokoll - Extra bolagsstämma - ${meetingDate}`,
    [GqlMeetingKind.ShareholdersMeeting]: `Protokoll - Bolagsstämma - ${meetingDate}`,
  }[meeting.kind];
  return filename;
};

const MeetingMinutesDocumentInner = ({
  meeting,
  hasActiveSubscription,
}: {
  meeting: Pick<GqlMeeting, 'id' | 'number' | 'startTime' | 'kind' | 'usesStepper'> & {
    latestMinutesDocument?: DisplayDocument | null;
  };
  hasActiveSubscription?: boolean;
}) => {
  const companyId = useCompanyId();
  const { navigate } = useNavigate();
  const apolloClient = useApolloClient();

  const [validationErrors, setValidationErrors] = useState<
    GqlDocGenValidateMeetingQuery['docGenValidateMeeting'] | null
  >(null);

  const defaultFilename = meetingFilename(meeting);

  const [showStripeDialog, setShowStripeDialog] = useState<{
    open: boolean;
    signatureMethod?: GqlDocumentSignatureMethod;
  }>({ open: false });

  const {
    data: { company },
  } = useCompanySuspenseQuery({ variables: { id: companyId } });

  const [doGetActiveSub] = useActiveCompanyStripeSubLazyQuery({ variables: { companyId }, fetchPolicy: 'no-cache' });

  const [fetchPaidFor] = useMeetingPaidForConcernsLazyQuery({ fetchPolicy: 'no-cache' });

  const [doGetValidate] = useDocGenValidateMeetingLazyQuery({
    variables: { companyId, meetingId: meeting.id },
    fetchPolicy: 'no-cache',
  });
  // eslint-disable-next-line sut/loading-mutation
  const [doGenerate] = useDocGenerateMeetingMinutesMutation({
    refetchQueries: [
      WarrantProgramDocument,
      ShareIssueDocument,
      AuthorizationDocument,
      MeetingMinutesWithUploadsDocument,
      OptionsDocumentsDocument,
      SiDocumentsShowDocument,
      FileDocument,
    ],
  });

  return (
    <>
      <Document
        hasActiveSubscription={hasActiveSubscription}
        defaultFileName={defaultFilename}
        document={meeting.latestMinutesDocument}
        allowDigitalSignature={meeting.usesStepper}
        validate={async () => {
          const { data } = await doGetValidate();
          if (!data) {
            return { isValid: false, errors: undefined };
          }
          const hasMeetingErrors = hasValidatorErrors(data.docGenValidateMeeting.validatorErrors);
          const hasAppendiceErrors = data.docGenValidateMeeting.appendices.some(a =>
            a.validatorStatuses.some(vs => vs.hasError || !vs.completed),
          );
          if (hasMeetingErrors || hasAppendiceErrors) {
            setValidationErrors(data.docGenValidateMeeting);
            return { isValid: false, errors: undefined };
          }
          return { isValid: true, errors: undefined };
        }}
        preview={async () => {
          return apiClient.previewMeetingMinutes(companyId, meeting.id);
        }}
        generate={
          meeting.usesStepper
            ? async signatureMethod => {
                const id = meeting?.id;
                if (!id) {
                  throw new Error('Unexpected state: no meetingId');
                }
                if (company.organizationNumber.startsWith('0')) {
                  return doGenerate({ variables: { companyId, meetingId: meeting.id, signatureMethod } });
                }
                const { data } = await fetchPaidFor({ variables: { companyId, id } });
                const paidFor = data?.meetingPaidForConcerns;
                if (!paidFor) {
                  throw new Error('Unexpected state: no paidFor');
                }
                const { data: activeSubData } = await doGetActiveSub();
                if (!activeSubData) {
                  throw new Error('Could not get active subscription');
                }
                const allMcs = [...paidFor.shareIssues, ...paidFor.warrantPrograms];
                const needsPayment = checkNeedsPayment(activeSubData.activeCompanyStripeSubscription, allMcs);
                if (needsPayment) {
                  setShowStripeDialog({ open: true, signatureMethod });
                  return;
                }
                await doGenerate({ variables: { companyId, meetingId: meeting.id, signatureMethod } });
              }
            : undefined
        }
        upload={async ({ file, signDigitally }) => {
          const relation = {
            relation_id: meeting.id,
            relation_name: DocGenRelationName.MeetingId,
          };
          await apiClient.uploadFile(
            file,
            file.name,
            companyId,
            relation,
            signDigitally,
            GqlDocSystemTagKind.MeetingProtocol,
          );
          return apolloClient.refetchQueries({
            include: [MeetingMinutesWithUploadsDocument, MeetingDocument],
          });
        }}
        signaturesSlot={
          meeting.latestMinutesDocument?.signatureMethod === GqlDocumentSignatureMethod.Digital && (
            <>
              <Divider />
              <DocumentSignatures
                documentId={meeting.latestMinutesDocument.id}
                scriveStatus={meeting.latestMinutesDocument.scriveStatus}
                zignedAgreementStatus={meeting.latestMinutesDocument.zignedAgreementStatus}
                signAllowedAction={meeting.latestMinutesDocument.signAllowedAction}
              />
            </>
          )
        }
      />

      {validationErrors && (
        <MeetingValidatorErrorDialog
          onClose={() => setValidationErrors(null)}
          meeting={meeting}
          meetingValidatorErrors={validationErrors}
        />
      )}
      {showStripeDialog.open && (
        <InnerStripe
          companyId={companyId}
          meetingId={meeting.id}
          onCancel={() => {
            setShowStripeDialog({ open: false });
          }}
          onPaid={() => {
            setShowStripeDialog({ open: false });
            return doGenerate({
              variables: {
                companyId,
                meetingId: meeting.id,
                signatureMethod: showStripeDialog.signatureMethod ?? GqlDocumentSignatureMethod.Analogue,
              },
            });
          }}
          onBillingRedirect={() => navigate('/settings/billing')}
        />
      )}
    </>
  );
};

export const MeetingMinutesDocument = (props: ComponentProps<typeof MeetingMinutesDocumentInner>) => {
  return (
    <Suspense>
      <MeetingMinutesDocumentInner {...props} />
    </Suspense>
  );
};
